'use client'
import * as React from 'react'
import { X, ShoppingBag, Star, CheckCircle2, Heart, Loader2, Instagram, Music2, Youtube, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import type { DemoProduct, DemoPost } from '@/lib/copy/demo-products'
import { cn } from '@/lib/utils'

// Demo-only modal. Mirrors the live customer-facing modal shape
// (CAI Store, Theater.xyz): vertical video on the left for video
// sources (static image fallback for photo posts), scrollable
// product detail panel on the right with name, price, options, Add
// to cart, and an Instagram-source block underneath crediting the
// creator + showing the original caption. Lifts the playground from
// "screenshot grid" to "click-through interaction" so visitors
// understand exactly what the widget feels like in production.

type Source = 'instagram' | 'tiktok' | 'youtube' | 'manual'

const SOURCE_ICON: Record<Source, typeof Instagram> = {
  instagram: Instagram,
  tiktok: Music2,
  youtube: Youtube,
  manual: Instagram,
}

export function AddToCartModal({
  product,
  post,
  onClose,
}: {
  product: DemoProduct | null
  // Optional originating post. When present, the modal shows the
  // video (if videoSrc) plus the social-source attribution block.
  post?: DemoPost | null
  onClose: () => void
}) {
  const [state, setState] = React.useState<'idle' | 'submitting' | 'added'>('idle')
  const [picks, setPicks] = React.useState<Record<string, string>>({})
  const [captionOpen, setCaptionOpen] = React.useState(false)
  const cardRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!product) return
    setState('idle')
    setCaptionOpen(false)
    setPicks(Object.fromEntries((product.options || []).map((o) => [o.label, o.values[0]])))

    // Remember what was focused so we can restore it on close, then move
    // focus into the dialog (the card) for an accessible open.
    const prevFocus = document.activeElement as HTMLElement | null
    const card = cardRef.current
    card?.focus()

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      // Focus trap: keep Tab cycling within the dialog's focusables.
      if (e.key === 'Tab' && card) {
        const focusables = card.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      prevFocus?.focus?.()
    }
  }, [product, onClose])

  if (!product) return null

  const source: Source = post?.user?.startsWith('@') ? 'instagram' : 'manual'
  const SourceIcon = SOURCE_ICON[source]
  const showVideo = Boolean(post?.videoSrc)
  // Decide aspect for the left rail: vertical 9:16 frame when video,
  // square 1:1 when image (matches the CAI Store + Theater.xyz feel
  // where reels are tall and photo posts are square).
  const leftAspect = showVideo ? 'aspect-[9/16]' : 'aspect-square'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="atc-title"
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200 sm:items-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      {/* Scoped preconnect: the video poster below is a raw unsplash URL,
          not routed through next/image, so this surface actually benefits. */}
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="anonymous" />
      {/* Outer close button, sits OUTSIDE the modal card on desktop
          so it doesn't fight the product image; falls back to inner
          on mobile where there's no real estate. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="fixed right-3 top-3 z-[101] hidden h-9 w-9 items-center justify-center rounded-full bg-background/95 text-foreground shadow-lg backdrop-blur transition hover:bg-secondary sm:flex"
      >
        <X className="h-4 w-4" />
      </button>

      <div
        ref={cardRef}
        tabIndex={-1}
        className="relative flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl border bg-card shadow-2xl outline-none motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-4 motion-safe:duration-[260ms] motion-safe:ease-[cubic-bezier(0.16,1,0.3,1)] sm:rounded-2xl sm:flex-row sm:motion-safe:slide-in-from-bottom-0"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm backdrop-blur transition hover:bg-secondary sm:hidden"
        >
          <X className="h-4 w-4" />
        </button>

        {/* === LEFT RAIL: video (or image) ===
            Stays fixed; never scrolls. On mobile sits on top of the
            product panel, on desktop side-by-side. */}
        <div className={`relative shrink-0 bg-black sm:w-[46%] ${leftAspect} sm:aspect-auto`}>
          {showVideo ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              src={post!.videoSrc}
              poster={post!.image}
              autoPlay
              muted
              loop
              playsInline
              controls
              className="h-full w-full object-contain"
            />
          ) : (
            <ImgWithFallback
              src={post?.image || product.image}
              alt={product.name}
              fill
              sizes="(max-width: 640px) 100vw, 46vw"
              className="object-cover"
            />
          )}
          {product.brand && !showVideo && (
            <span className="absolute left-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-medium uppercase tracking-widest text-foreground backdrop-blur">
              {product.brand}
            </span>
          )}
        </div>

        {/* === RIGHT RAIL: scrollable product + source block === */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
          <div className="flex flex-col gap-4 p-5 sm:p-6">
            <div>
              <h3 id="atc-title" className="font-display text-xl font-medium leading-tight md:text-2xl">{product.name}</h3>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <span className="font-display text-lg font-medium md:text-xl">{product.price}</span>
                {product.compareAt && (
                  <span className="text-sm text-muted-foreground line-through">{product.compareAt}</span>
                )}
                {product.discountLabel && (
                  <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                    {product.discountLabel}
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className={cn('h-3 w-3', i < Math.round(product.rating) ? 'fill-amber-400 text-amber-400' : 'text-muted')} />
                ))}
                <span className="ml-0.5">{product.rating} · {product.reviews.toLocaleString()} reviews</span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">{product.blurb}</p>

            {product.options?.map((opt) => (
              <div key={opt.label}>
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{opt.label}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {opt.values.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setPicks((p) => ({ ...p, [opt.label]: v }))}
                      className={cn(
                        'rounded-md border px-3 py-1.5 text-xs font-medium transition',
                        picks[opt.label] === v
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-background hover:border-primary/40 hover:bg-secondary',
                      )}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex items-stretch gap-2 pt-1">
              <Button
                type="button"
                disabled={state !== 'idle'}
                onClick={() => {
                  setState('submitting')
                  setTimeout(() => setState('added'), 600)
                }}
                className="flex-1"
              >
                {state === 'idle'       && (<><ShoppingBag className="mr-1 h-4 w-4" /> Add to bag · {product.price}</>)}
                {state === 'submitting' && (<><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Adding…</>)}
                {state === 'added'      && (<><CheckCircle2 className="mr-1 h-4 w-4" /> Added! (demo)</>)}
              </Button>
              <Button type="button" variant="outline" size="icon" aria-label="Save"><Heart className="h-4 w-4" /></Button>
            </div>
          </div>

          {/* === Social source attribution ===
              Matches the CAI Store / Theater.xyz pattern: platform
              icon, caption (truncated + expandable), creator credit,
              and a link out to the original post when present. */}
          {post && (
            <div className="mt-auto border-t bg-secondary/30 p-5 text-sm sm:p-6">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                  <SourceIcon className="h-4 w-4" />
                  Source · {source === 'instagram' ? 'Instagram' : source}
                </span>
                {post.user && (
                  <span className="text-[12px] font-semibold text-foreground">{post.user}</span>
                )}
              </div>
              {post.caption && (
                <p className={cn('text-[13px] leading-relaxed text-muted-foreground', !captionOpen && 'line-clamp-3')}>
                  {post.caption}
                </p>
              )}
              {post.caption && post.caption.length > 120 && (
                <button
                  type="button"
                  onClick={() => setCaptionOpen((v) => !v)}
                  className="mt-1 text-[12px] font-medium text-primary hover:underline"
                >
                  {captionOpen ? 'Show less' : 'Show more'}
                </button>
              )}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
                <span>Rights-cleared · sourced via Idukki</span>
                {post.originalUrl && (
                  <a
                    href={post.originalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                  >
                    View original post <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              <p className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                This is a live preview · no real order is placed
              </p>
            </div>
          )}
          {!post && (
            <div className="mt-auto border-t bg-secondary/30 px-5 py-4 text-[10px] uppercase tracking-widest text-muted-foreground sm:px-6">
              This is a live preview · no real order is placed
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
